/**
 * Library of miscellaneous functions used for a11y
 */
const A11yUtil = (function () {
    //  Enum of common keycodes used for A11y (add more as needed, in alphabetical order)
    const _KEYCODES = {
        backspace: 8,
        dash: 189,
        delete: 46,
        divide: 111,
        down: 40,
        end: 35,
        enter: 13,
        escape: 27,
        forwardSlash: 191,
        left: 37,
        minus: 173, // this is the minus key in firefox (in chrome it is 189)
        numpadZero: 96,
        numpadNine: 105,
        right: 39,
        shift: 16,
        spacebar: 32,
        subtract: 109,
        tab: 9,
        up: 38,
        w: 87,
        zero: 48,
        nine: 57
    };

    const _MOUSECLICKS = {
        LEFT: 1,
        MIDDLE: 2,
        RIGHT: 3
    };

    const _APP = {
        CARE_COORDINATION: 'CARE_COORDINATION',
        POPULATION_HEALTH: 'POPULATION_HEALTH'
    };

    return {
        APP: _APP,
        KEYCODES: _KEYCODES,
        MOUSECLICKS: _MOUSECLICKS,

        accordionKeyDown: _accordionKeyDown,
        findKendoAutoCompletesToA11yEnhance: _findKendoAutoCompletesToA11yEnhance,
        findKendoDatePickersToA11yEnhance: _findKendoDatePickersToA11yEnhance,
        findKendoDropDownListsToA11yEnhance: _findKendoDropDownListsToA11yEnhance,
        fixA11yViolationsOnScreenLoad: _fixA11yViolationsOnScreenLoad,
        fixKendoAutoCompletesForA11y: _fixKendoAutoCompletesForA11y,
        fixKendoDatePickerForA11y: _fixKendoDatePickerForA11y,
        fixKendoAutoMultiSelectsForA11y: _fixKendoAutoMultiSelectsForA11y,
        fixKendoSchedulerDatePicker: _fixKendoSchedulerDatePicker,
        fixTableRoles: _fixTableRoles,
        fixKendoItemsPerPageBug: _fixKendoItemsPerPageBug,
        formatStandardTelephoneNumber: _formatStandardTelephoneNumber,
        getHyperlinkEmail: _getHyperlinkEmail,
        getHyperlinkTelephoneNumber: _getHyperlinkTelephoneNumber,
        handleKeyboardClick: _handleKeyboardClick,
        prettifyNativeSelect: _prettifyNativeSelect,
        setFocus: _setFocus,
        wrapNavigationArea: _wrapNavigationArea,
        setAutocompleteAttributes: _setAutocompleteAttributes,
        setAriaCurrentAttribute: _setAriaCurrentAttribute,
        toggleKendoDatepickerDisabled: _toggleKendoDatepickerDisabled
    };

    /**
     * Function which can be called within an accordion so that an enter key space key is treated as a click event.
     *
     * Example: onkeydown="A11yUtil.accordionKeyDown(event,this)"
     *
     * @param {any} e
     * @param {any} val
     */
    function _accordionKeyDown(e, val) {
        if (e.type === 'keydown' && (e.keyCode === A11yUtil.KEYCODES.enter || e.keyCode === A11yUtil.KEYCODES.spacebar)) {
            $(val).trigger('click');
        }
    }

    /**
     * Function which can be called after a screen loads or other key times to dynamically fix a11y violations on the fly
     * via Javascript/jQuery. This function should make calls to other functions which perform a11y-specific enhancements.
     * @param {object} containerSelector optional css selector for container you want to limit the search by (Default: entire document)
     */
    function _fixA11yViolationsOnScreenLoad(containerSel) {
        DevUtils.logVerbose('A11yUtil.fixA11yViolationsOnScreenLoad("' + containerSel + '")', null, null, 'green');

        if (!containerSel || containerSel === '') {
            DevUtils.logWarn('No containerSel passed in - this may have a negative impact on performance therefore execution of this function will not proceed', 'A11yUtil.fixA11yViolationsOnScreenLoad()');
            return;
        }

        _fixTableRoles(containerSel);
        _findKendoDatePickersToA11yEnhance(containerSel);
        _findKendoAutoCompletesToA11yEnhance(containerSel);
        _findKendoDropDownListsToA11yEnhance(containerSel);
        A11yForms.fixRequiredFields(null, containerSel);
        A11yForms.addRequiredIndicatorNote(containerSel);
    }

    /**
     * This function will find and loop through all all kendo autoComplete fields on a screen and make a subsequent function call to correct
     * each autoComplete to meet a11y guidelines. This function is needed because the Kendo autocomplete component is not a11y-compliant
     * out of the box.
     * @param {object} containerSelector optional css selector for container you want to limit the search by (Default: entire document)
     */
    function _findKendoAutoCompletesToA11yEnhance(containerSelector) {
        containerSelector = containerSelector ? containerSelector + ' ' : '';
        DevUtils.logDebug('Searching for Kendo autoCompletes to enhance...', 'A11yUtil.findKendoAutoCompletesToA11yEnhance()', { containerSelector: containerSelector }, 'green');

        // loop through each Kendo autocomplete found and fix some a11y-related things
        $(containerSelector + '.k-autocomplete').each(function (index, autoCompleteContainer) {
            _fixKendoAutoCompletesForA11y(autoCompleteContainer);
        });

        // kendoAutoCompletes inside collapsed accordions are not "seen" on screen load, so we need to search for more when an accordion is expanded:
        $(containerSelector + '.ah-accordion-btn').on('click', function () {
            let _this = this;
            // the kendoAutoCompletes are currently hidden but will soon become visible...
            setTimeout(function () {
                let accordionContentId = $(_this).attr('aria-controls');
                if (accordionContentId && $('#' + accordionContentId).is(':visible')) {
                    DevUtils.logDebug('Accordion was expanded... searching for more Kendo autoCompletes to enhance...', 'A11yUtil.findKendoAutoCompletesToA11yEnhance()', { accordionContentId: accordionContentId }, 'green');

                    $('#' + accordionContentId + ' .k-autocomplete').each(function (index, autoCompleteContainer) {
                        _fixKendoAutoCompletesForA11y(autoCompleteContainer);
                    });
                }
            }, 500);
        });
    }

    /**
     *  Fix the Kendo autocomplete component in the specified autoCompleteContainer.
     *  @param {object} autoCompleteContainer element representing the Kendo autoComplete container, i.e. the element assigned the .k-autocomplete CSS class
     */
    function _fixKendoAutoCompletesForA11y(autoCompleteContainer) {
        if (!$(autoCompleteContainer).hasClass('a11y-enhanced')) {
            $(autoCompleteContainer).addClass('a11y-enhanced');

            let inputElement = $(autoCompleteContainer).find('input.k-input[data-role="autocomplete"]');
            let inputId = $(inputElement).attr('id');
            let clearButton = $(autoCompleteContainer).find('span.k-clear-value[role="button"]');

            DevUtils.logDebug('fixing Kendo autoComplete [' + inputId + ']', 'A11yUtil.fixKendoAutoCompletesForA11y()', autoCompleteContainer, 'green');

            if (inputElement) {
                // add aria-haspopup attribute, remove aria-owns & role attributes
                $(inputElement).attr('aria-haspopup', 'listbox').removeAttr('aria-owns').removeAttr('role');
            }

            // Remove the clearButton
            if (clearButton) {
                $(clearButton).remove();
            }

            // add SR instructions on how to use autocompletes
            $(autoCompleteContainer).append('<div id="' + inputId + '_instructions" style="display:none;">Begin typing to search, use arrow keys to navigate search results, Enter to select.</div>');
            $(inputElement).attr('aria-describedby', inputId + '_instructions');

            // set up live "X results found" region
            $(autoCompleteContainer).append('<div id="' + inputId + '_resultsFound" class="results-count sr-only" aria-live="assertive"></div>');

            // set up sr-only element to hold how many items checked (this will be updated after a search, but initialize it to the original value)
            $(autoCompleteContainer).append('<span id="' + inputId + '_placeholder" class="sr-only"></span>');
            $('#' + inputId + '_placeholder').text($(inputElement).attr('placeholder'));

            let kendoAutocomplete = $("#" + inputId).data("kendoAutoComplete");

            if (kendoAutocomplete) {
                kendoAutocomplete.bind('dataBound', function (e) {
                    // once data has been retrieved from datasource, update the number of results found so SR can announce it

                    DevUtils.logDebug('databound event for [' + inputId + '], calculating number of results found', 'A11yUtil.fixKendoAutoCompletesForA11y()', e, 'green');

                    $(autoCompleteContainer).find('.results-count').text('');

                    let items = kendoAutocomplete.items();
                    if (items) {
                        let numItems = items.length;
                        let resultsText;

                        DevUtils.logDebug(numItems + ' results found for [' + inputId + ']', 'A11yUtil.fixKendoAutoCompletesForA11y()', { items: items }, 'green');

                        if (numItems === 1) {
                            resultsText = 'One result found';
                        } else if (numItems > 1) {
                            resultsText = numItems + ' results found';
                        } else {
                            resultsText = 'No results found';
                        }

                        // update the live region with the num results found so the SR announces it
                        $(autoCompleteContainer).find('.results-count').text(resultsText);
                    }

                    // remove tabindex -1 from the results list <ul> element
                    $('ul#' + inputId + '_listbox').removeAttr('tabindex');
                    $('ul#' + inputId + '_listbox li').removeAttr('tabindex');
                });

                kendoAutocomplete.bind('close', function () {
                    // unset the num of results found so the SR does not announce it if/when user comes back to this field
                    $(autoCompleteContainer).find('.results-count').text('');
                });
            }
        }
    }

    /**
     * This function will find and loop through all all kendo datepickers on a screen and make a subsequent function call to correct
     * each datepicker to meet a11y guidelines. This function is needed because the Kendo Datepicker component is not a11y-compliant
     * out of the box.
     * @param {object} containerSelector optional css selector for container you want to limit the search by (Default: entire document)
     */
    function _findKendoDatePickersToA11yEnhance(containerSelector) {
        containerSelector = containerSelector ? containerSelector + ' ' : '';

        DevUtils.logDebug('Searching for Kendo datepickers to enhance...', 'A11yUtil.findKendoDatePickersToA11yEnhance()', { containerSelector: containerSelector }, 'green');

        // loop through each Kendo datepicker found and fix some a11y-related things
        $(containerSelector + '.k-datepicker').each(function (_index, datepickerContainer) {
            _fixKendoDatePickerForA11y(datepickerContainer);
        });

        $(containerSelector + '.k-datetimepicker').each(function (_index, datepickerContainer) {
            _fixKendoDateTimePickerForA11y(datepickerContainer);
        });

        // datepickers inside collapsed accordions are not "seen" on screen load, so we need to search for more when an accordion is expanded:
        $(containerSelector + '.ah-accordion-btn').on('click', function () {
            let _this = this;
            // the accordion is currently hidden but will soon become visible...
            setTimeout(function () {
                let accordionContentId = $(_this).attr('aria-controls');
                if (accordionContentId && $('#' + accordionContentId).is(':visible')) {
                    DevUtils.logDebug('Accordion was expanded... searching for more Kendo datepickers to enhance...', 'A11yUtil.findKendoDatePickersToA11yEnhance()', null, 'green');

                    $('#' + accordionContentId + ' .k-datepicker').each(function (_index, datepickerContainer) {
                        _fixKendoDatePickerForA11y(datepickerContainer);
                    });

                    $('#' + accordionContentId + ' .k-datetimepicker').each(function (_index, datepickerContainer) {
                        _fixKendoDateTimePickerForA11y(datepickerContainer);
                    });
                }
            }, 500);
        });
    }

    /**
     * This function will find and loop through all all kendo DropDownList's on a screen and make a subsequent function call to correct
     * each one to meet a11y guidelines. This function is needed because the Kendo DropDownList component is not a11y-compliant
     * out of the box.
     * @param {object} containerSelector optional css selector for container you want to limit the search by (Default: entire document)
     */
    function _findKendoDropDownListsToA11yEnhance(containerSelector) {
        containerSelector = containerSelector ? containerSelector + ' ' : '';

        DevUtils.logDebug('Searching for Kendo DropDownLists to enhance...', 'A11yUtil.findKendoDropDownListsToA11yEnhance()', { containerSelector: containerSelector } , 'green');

        // loop through each Kendo dropdownlist found and fix some a11y-related things
        $(containerSelector + '.k-dropdown[role="listbox"]').each(function (index, dropdownContainer) {
            _fixKendoFilterDropDownListsForA11y(dropdownContainer);
        });

        // kendoDropdowns inside collapsed accordions are not "seen" on screen load, so we need to search for more when an accordion is expanded:
        $(containerSelector + '.ah-accordion-btn').on('click', function () {
            let _this = this;
            // the kendoDropdowns are currently hidden but will soon become visible...
            setTimeout(function () {
                let accordionContentId = $(_this).attr('aria-controls');
                if (accordionContentId && $('#' + accordionContentId).is(':visible')) {
                    DevUtils.logDebug('Accordion was expanded... searching for more KendoDropDowns to enhance...', 'A11yUtil.findKendoDropDownListsToA11yEnhance()', { accordionContentId: accordionContentId }, 'green');

                    $('#' + accordionContentId + ' .k-dropdown[role="listbox"]').each(function (index, dropdownContainer) {
                        _fixKendoFilterDropDownListsForA11y(dropdownContainer);
                    });
                }
            }, 500);
        });
    }

    /**
     *  Fix the Kendo dropdown component with the Filter functionality in the specified dropdownContainer.
     *  @param {object} dropdownContainer element representing the Kendo dropdown container, i.e. the element assigned the .k-dropdown CSS class
     */
    function _fixKendoFilterDropDownListsForA11y(dropdownContainer) {
        if (!$(dropdownContainer).hasClass('a11y-enhanced')) {
            let inputElement = $(dropdownContainer).find('[data-role="dropdownlist"]');
            let inputId = $(inputElement).attr('id');

            if (inputId) {
                let kendoDropdown = $(inputElement).data("kendoDropDownList");

                if (kendoDropdown) {
                    if (inputElement.attr('required')) {
                        $(dropdownContainer).attr('aria-required', 'true');
                    }

                    let filterDropdown = kendoDropdown.filterInput;

                    if (filterDropdown) {
                        // remove one of the attrs on the kendo dropdownlist which is causing every single item in the list to be announced everytime it is filtered
                        $(dropdownContainer).addClass('a11y-enhanced').removeAttr('aria-live');

                        DevUtils.logDebug('fixing Kendo filter dropdown [' + inputId + ']', 'A11yUtil.fixKendoFilterDropDownListsForA11y()', dropdownContainer, 'green');

                        // add SR instructions on how to use dropdowns
                        $(dropdownContainer).attr('aria-describedby', inputId + '_instructions').append('<div id="' + inputId + '_instructions" style="display:none;">Press Space to open list, use arrows to navigate, Enter to select item, Escape to close list.</div>');

                        // set up live "X results found" region
                        $(dropdownContainer).append('<div id="' + inputId + '_resultsFound" class="results-count sr-only" aria-live="assertive"></div>');

                        kendoDropdown.bind('dataBound', function (e) {
                            // once data has been retrieved from datasource, update the number of results found so SR can announce it
                            DevUtils.logDebug('databound event for [' + inputId + '], calculating number of results found', 'A11yUtil.fixKendoFilterDropDownListsForA11y()', e, 'green');

                            $(dropdownContainer).find('.results-count').text('');

                            let items = kendoDropdown.items();
                            if (items) {
                                let numItems = items.length;
                                let resultsText;

                                DevUtils.logDebug(numItems + ' results found for [' + inputId + ']', 'A11yUtil.fixKendoFilterDropDownListsForA11y()', { items: items }, 'green');

                                if (numItems === 1) {
                                    resultsText = 'One result found';
                                } else if (numItems > 1) {
                                    resultsText = numItems + ' results found';
                                } else {
                                    resultsText = 'No results found';
                                }

                                // update the live region with the num results found so the SR announces it
                                $(dropdownContainer).find('.results-count').text(resultsText);
                            }
                        });

                        kendoDropdown.bind('open', function () {
                            $('#' + inputId + '_listbox').each(function (index, list) {
                                // remove the attr on the kendo dropdownlist which is causing every single item in the list to be announced everytime it is filtered
                                $(list).removeAttr('aria-live');
                            });
                        });

                        kendoDropdown.bind('close', function () {
                            // unset the num of results found so the SR does not announce it if/when user comes back to this field
                            $(dropdownContainer).find('.results-count').text('');
                        });
                    }
                }
            }
        }
    }

    /**
     * Function to make a1ly corrections on a specific Kendo datepicker component. We are making only the necessary enhancements as required
     * by A360 to make this element minimally-compliant. Since users can still enter dates via keyboard (without using the actual popup
     * datepicker), A360 has agreed to make datepicker-specific violations a P3 severity (which do not need to be fixed to obtain Letter of Conformance)
     * @param {object} datepickerContainer element representing the Kendo datepicker container, i.e. the element assigned the .k-datepicker CSS class
     */
    function _fixKendoDatePickerForA11y(datepickerContainer) {
        let datepickerId = $(datepickerContainer).find('input.k-input').attr('id');
        let datepicker = $('#' + datepickerId).data('kendoDatePicker');
        let datepickerInputField = $(datepickerContainer).find('.k-picker-wrap .k-input');
        let calendarBtn = $(datepickerContainer).find('.k-picker-wrap .k-select[role="button"]');

        if (!$(datepickerContainer).hasClass('a11y-enhanced')) {
            $(datepickerContainer).addClass('a11y-enhanced');

            DevUtils.logDebug('fixing Kendo datepicker [' + datepickerId + ']', 'A11yUtil.fixKendoDatePickerForA11y()',
                { datepickerContainer: datepickerContainer, datepicker: datepicker, datepickerInputField: datepickerInputField, calendarBtn: calendarBtn }, 'green');

            _fixKendoDateButton(calendarBtn, datepickerInputField);

            // set aria-expanded=false on trigger button when calendar is closed
            if (datepicker) {
                datepicker.bind('close', function () {
                    setTimeout(function () {
                        _datepickerToggleAriaExpanded(calendarBtn, datepickerInputField);
                    }, 500);
                });
            }
        } else {
            // check for newly disabled date picker so we can disable tabbing
            if ($(datepickerInputField).prop('disabled')) {
                $(calendarBtn).removeAttr('tabindex');
            } else {
                $(calendarBtn).attr('tabindex', '0');
            }
        }
    }

    /**
     * Function to make a1ly corrections on a specific Kendo datetimepicker component. We are making only the necessary enhancements as required
     * by A360 to make this element minimally-compliant. Since users can still enter dates via keyboard (without using the actual popup
     * datetimepicker), A360 has agreed to make datepicker-specific violations a P3 severity (which do not need to be fixed to obtain Letter of Conformance)
     * @param {object} dateTimePickerContainer element representing the Kendo datetimepicker container, i.e. the element assigned the .k-datetimepicker CSS class
     */
    function _fixKendoDateTimePickerForA11y(dateTimePickerContainer) {
        let datepickerId = $(dateTimePickerContainer).find('input.k-input').attr('id');
        let datepicker = $('#' + datepickerId).data('kendoDateTimePicker');
        let datepickerInputField = $(dateTimePickerContainer).find('.k-picker-wrap .k-input');
        let calendarBtn = $(dateTimePickerContainer).find('.k-picker-wrap .k-select .k-link-date[role="button"]');
        let timeBtn = $(dateTimePickerContainer).find('.k-picker-wrap .k-select .k-link-time[role="button"]');

        if (!$(dateTimePickerContainer).hasClass('a11y-enhanced')) {
            $(dateTimePickerContainer).addClass('a11y-enhanced');

            DevUtils.logDebug('fixing Kendo datetimepicker [' + datepickerId + ']', 'A11yUtil.fixKendoDateTimePickerForA11y()',
                { dateTimePickerContainer: dateTimePickerContainer, datepicker: datepicker, datepickerInputField: datepickerInputField, calendarBtn: calendarBtn, timeBtn: timeBtn }, 'green');

            // remove unnecessary ARIA attributes
            $(datepickerInputField).removeAttr('role aria-expanded aria-owns');

            _fixKendoDateButton(calendarBtn, datepickerInputField);
            _fixKendoTimeButton(timeBtn, datepickerInputField);

            // set aria-expanded=false on trigger button when calendar is closed
            if (datepicker) {
                datepicker.bind('close', function () {
                    setTimeout(function () {
                        _datepickerToggleAriaExpanded(calendarBtn, datepickerInputField);
                        _datepickerToggleAriaExpanded(timeBtn, datepickerInputField);
                    }, 500);
                });
            }
        } else {
            // check for newly disabled date picker so we can disable tabbing
            if ($(datepickerInputField).prop('disabled')) {
                $(calendarBtn).removeAttr('tabindex');
                $(timeBtn).removeAttr('tabindex');
            } else {
                $(calendarBtn).attr('tabindex', '0');
                $(timeBtn).attr('tabindex', '0');
            }
        }
    }

    /**
     *  Function to make a11y corrections to the kendoMultiSelect component. This function will get called from GCHomeCommon.bindKendoMultiSelect(), which is a
     *  common function used to instantiate the component on various screens.
     * @param {any} kendoMultiSelect the object representing the kendoMultiSelect component after it has been instantiated with the Kendo config
     */
    function _fixKendoAutoMultiSelectsForA11y(kendoMultiSelect) {
        if (kendoMultiSelect) {
            let multiSelectContainer = kendoMultiSelect.wrapper;

            if (!$(multiSelectContainer).hasClass('a11y-enhanced')) {
                let inputElement = $(multiSelectContainer).find('.k-multiselect-wrap input.k-input');
                let inputId = $(inputElement).attr('aria-controls');

                $(multiSelectContainer).addClass('a11y-enhanced');

                DevUtils.logDebug('fixing Kendo multiselect [' + inputId + ']', 'A11yUtil.fixKendoAutoMultiSelectsForA11y()', multiSelectContainer, 'green');

                // add SR instructions on how to use multiselects
                safeFind(multiSelectContainer).append('<div id="' + inputId + '_instructions" style="display:none;">Begin typing to search, use up and down arrow keys to navigate search results. Results will show a list of modifier codes and descriptions. Press Enter to select. To remove options, use left and right arrow keys to highlight desired option and then press Delete key.</div>');
                $(inputElement).attr('aria-describedby', inputId + '_instructions');

                if (isMultSelectRequired(multiSelectContainer)) {
                    $(inputElement).attr('required', 'required');
                }

                // set up live "X results found" region
                safeFind(multiSelectContainer).append('<div id="' + inputId + '_resultsFound" class="results-count sr-only" aria-live="assertive"></div>');

                $(multiSelectContainer).find('.k-clear-value').attr({ 'tabindex': '0', 'onkeydown': 'A11yUtil.handleKeyboardClick(event, this);' , 'aria-label': 'clear modifiers'});

                kendoMultiSelect.bind('open', function (e) {
                    // once data has been retrieved from datasource, update the number of results found so SR can announce it
                    DevUtils.logDebug('open event for [' + inputId + '], calculating number of results found', 'A11yUtil.fixKendoAutoMultiSelectsForA11y()', e, 'green');

                    $(multiSelectContainer).find('.results-count').text('');

                    let items = kendoMultiSelect.items();
                    if (items) {
                        let numItems = $(items, this).not('.k-state-selected').length;
                        let resultsText;

                        DevUtils.logDebug(numItems + ' results found for [' + inputId + ']', 'A11yUtil.fixKendoAutoMultiSelectsForA11y()', { items: items }, 'green');

                        if (numItems === 1) {
                            resultsText = 'One result found';
                        } else if (numItems > 1) {
                            resultsText = numItems + ' results found';
                        } else {
                            resultsText = 'No results found';
                        }

                        // update the live region with the num results found so the SR announces it
                        $(multiSelectContainer).find('.results-count').text(resultsText);
                    }
                });

                kendoMultiSelect.bind('close', function () {
                    // unset the num of results found so the SR does not announce it if/when user comes back to this field
                    $(multiSelectContainer).find('.results-count').text('');
                });
            }
        }
    }

    function isMultSelectRequired(multiSelectContainer) {
        let isRequired = false;

        let parentDiv = $(multiSelectContainer).closest('.a11yField');
        if (parentDiv.length > 0) {
            let labelElem = $(parentDiv).find('label');
            if (labelElem.length > 0) {
                isRequired = $(labelElem).hasClass('ah-required');
            }
        }

        return isRequired;

    }

    /**
     * This function fixes a bug found on the Kendo grid pager after the upgrade to Kendo 2022 version. On some grids, it is observed
     * that the "items per page" dropdown does not appear. Instead, we get a dropdown allowing users to choose which page to navigate
     * to. One workaround I found is to call kendoGrid.pager.resize() to trigger a resize event which then magically fixes it. You can
     * see more details here: https://stackoverflow.com/questions/71530889/on-the-initial-page-load-kendo-ui-pager-is-not-showing-pagesizes-drop-down
     * and here: https://stackoverflow.com/questions/64515365/items-per-page-and-0-1-of-2-in-kendo-is-not-displaying-on-ui-until-i/64527934#64527934.
     *
     * In order to fix this, you must call this function inside the grid databound function. For a11y-enhanced grids, this is already
     * being done. For non-a11y-enhanced grids, you will have to make an explicit call to this function to fix it, unless you can figure
     * out a better way...
     * @param {any} gridName
     */
    function _fixKendoItemsPerPageBug(gridName) {
        // hack to fix the problem with the "items per page" not showing up
        if ($('#' + gridName).length && $('#' + gridName).data('kendoGrid') && $('#' + gridName).data('kendoGrid').pager) {
            $('#' + gridName).data('kendoGrid').pager.resize();
        } else {
            DevUtils.logWarn('could not find grid with name [' + gridName + ']', 'A11yUtil.fixKendoItemsPerPageBug()');
        }
    }

    /**
     * Function to make a11y corrections on the datepicker embedded in the Kendo Scheduler component. This datepicker is a little bit different
     * than the standard datepicker, and so it needs to handled differently. This function should get called in the databound callback function
     * when a Kendo Scheduler is instantiated.
     */
    function _fixKendoSchedulerDatePicker() {
        // the datepicker calendar view is not instantiated until the user clicks the button to do so; this block of code is programmatically
        // causing that to happen so that we can then assign an ID attribute to the calendar popup
        let scheduler = $("#scheduler").data("kendoScheduler");
        scheduler._showCalendar();
        $('.k-animation-container').css('display', 'none');
        scheduler.popup.close();

        let calendarContainer = $(scheduler.calendar.element).parent();

        setTimeout(function () {
            let datepickerBtn = $('.k-scheduler .k-scheduler-toolbar a.k-nav-current');
            if (!$(datepickerBtn).hasClass('a11y-enhanced')) {
                $(datepickerBtn).addClass('a11y-enhanced');

                DevUtils.logDebug('fixing Kendo Scheduler datepicker [scheduler]', 'A11yUtil.fixKendoSchedulerDatePicker()', datepickerBtn, 'green');

                // by default, Kendo does not give an ID to the calendar dateview on the Scheduler, so give it one...
                $(calendarContainer).attr('id', 'scheduler_dateview');

                // ...and then use that same ID to set aria-controls on the trigger button
                $(datepickerBtn).attr('aria-controls', 'scheduler_dateview');

                // initialize aria-expanded on the trigger button
                $(datepickerBtn).attr('aria-expanded', 'false');

                // make sr announce which view button is clicked (Day, Week or Month)
                let viewButtonSpan = $(scheduler.element).find('.k-scheduler-views');
                let srOnlyElem = $('<span class="sr-only" aria-live="polite" aria-atomic="true">calendar is now in Day view mode</span>').prependTo($(viewButtonSpan));
                $(viewButtonSpan).find('button').on('click', function () {
                    $(srOnlyElem).text('calendar is now in ' + $(this).text() + ' view mode');
                });

                // make the calendar button respond to Enter and Spacebar keypress
                $(datepickerBtn).off('keydown').on('keydown', function (e) {
                    _datepickerBtnKeydown(e, this);
                });

                // set aria-expanded on trigger button when calendar button is clicked
                $(datepickerBtn).on('click', function () {
                    $(datepickerBtn).attr('aria-expanded', 'true');

                    setTimeout(function () {
                        $(calendarContainer).find('.k-calendar-view .k-content.k-month tbody td a.k-link').each(function () {
                            $(this).attr( 'aria-label', $(this).attr('title'));
                        });

                        $(calendarContainer).find('.k-calendar-view .k-content.k-month thead th').each(function () {
                            $(this).attr('aria-label', $(this).attr('title'));
                        });
                    });

                });

                // set aria-expanded to false when calendar navigates to different date (i.e. user selects date in cal)
                scheduler.bind('navigate', function () {
                    $(datepickerBtn).attr('aria-expanded', 'false');
                });

                // set aria-expanded=false on trigger button when calendar loses focus
                $('#scheduler_dateview').on('focusout', function () {
                    $(datepickerBtn).attr('aria-expanded', 'false');
                });

                // adding sr-only msg to the Today, Previous, Next, Day, Week, Month buttons.
                $('<span class="sr-only" id="srCalendarNavMsg">activating this element will cause content on the table to be updated.</span>').prependTo($('.k-scheduler-navigation'));
                $('.k-button.k-nav-today, .k-button.k-nav-prev, .k-button.k-nav-next, .k-button.k-view-day, .k-button.k-view-week, .k-button.k-view-month').attr('aria-describedby', 'srCalendarNavMsg');
            }
        }, 1000);
    }

    /**
     * Function to add the attribute role="presentation" to all layout-only tables (i.e. tables that do not contain grid data).
     * The vast majority of <table>'s used in the app are presentational in nature, which is very bad practice since screen readers
     * will try to read and interpret them as data grids. Adding role="presentation" to the table will instruct the screen reader to
     * skip it. There are thousands of impproper usages of the <table> element - too many to fix manually - so we are using jquery
     * to fix these after a page has been loaded and/or changed.
     *
     * NOTE 1:
     * For now, we have hooked into several places in the UI where a function is called to display the "Loading..." element in order
     * to call this function. This seems to be catching a huge amount of table elements, but probably not all. We will catch those
     * we missed as we notice them.
     *
     * NOTE 2:
     * We may need to adjust the tableSelector if we notice that this function becomes overzealous and tags too many tables as
     * presentational.
     *
     * @param {object} containerSelector optional css selector for container you want to limit the search by (Default: entire document)
     */
    function _fixTableRoles(containerSelector) {
        containerSelector = containerSelector ? containerSelector + ' ' : '';

        const tableSelector = '.a11y-k-grid, [role="presentation"], [role="grid"], .k-scheduler-table, .k-scheduler-layout, .GlbGrid, .DecHisGrid, .DBGrid';

        if (DevUtils.isDevMode()) {
            DevUtils.logVerbose('Adding role=presentation to [' + $(containerSelector + 'table').not(tableSelector).length + '] table elements', 'A11yUtil.fixTableRoles()', { containerSelector: containerSelector });
        }

        // add role="presentation" to the appropriate <table> elements
        $(containerSelector + 'table').not(tableSelector).attr("role", "presentation");
    }

    /**
     * Returns an E.164 formatted telephone number suitable for including in an href, e.g. <a href="tel:<formattedNumber>">.
     * As an example, this will convert something like 614-867-5309 into +16148675309. For now, unless the country code is
     * already part of the number, we will assume that the country code should be +1 (U.S.)
     * @param {string} phoneNumber telephone number to format
     */
    function _formatStandardTelephoneNumber(phoneNumber) {
        let formattedNumber = '';

        // format the phone number in E.164 format and assume country code is always the US
        if (!phoneNumber.startsWith('+')) {
            // assume it is not already in E.164 format
            if (phoneNumber.startsWith('1')) {
                // the US country code is already added, just add the '+' sign
                formattedNumber += '+' + phoneNumber;
            } else {
                // add the US country code
                formattedNumber += '+1' + phoneNumber;
            }
        } else {
            // if a '+' sign is already present then assume the number is already in E.164 format
            formattedNumber += phoneNumber;
        }

        // remove any hyphens, parens and whitespace from the href telephone number and return it
        return formattedNumber.replace(/[-()\s]/g, '');
    }

    /**
     * Return an email hyperlink in string format (e.g. <a href="mailto:mike@email.com">mike@gmail.com</a>)
     * @param {object} emailElem
     */
    function _getHyperlinkEmail(emailElem) {
        let emailLink = '';

        if (emailElem && $(emailElem).length === 1 && !$(emailElem).is('a') && $(emailElem).find('a').length === 0) {
            let emailText = $(emailElem).text();

            // GCPROD-34233: Do a quick check to see if the value might be an email address.
            if (emailText.includes("@") && emailText.includes(".") && emailText !== 'Not Available') {
                emailLink = '<a href="mailto:' + emailText + '">' + emailText + '</a>';
            } else {
                emailLink = emailText;
            }
        }

        return emailLink;
    }

    /**
     * Return an E.164-formatted telephone hyperlink in string format (e.g. <a href="tel:+16148675309">614-867-5309</a>)
     * @param {object} phoneNumberElem
     */
    function _getHyperlinkTelephoneNumber(phoneNumberElem) {
        let phoneNumberLink = '';

        if (phoneNumberElem && $(phoneNumberElem).length === 1 && !$(phoneNumberElem).is('a') && $(phoneNumberElem).find('a').length === 0) {
            let numberText = $(phoneNumberElem).text();
            if (numberText !== '' && numberText !== 'N/A' && numberText !== 'Not Available') {
                phoneNumberLink = '<a href="tel:' + _formatStandardTelephoneNumber(numberText) + '">' + numberText + '</a>';
            } else {
                phoneNumberLink = numberText;
            }
        }

        return phoneNumberLink;
    }

    /**
     * Chrome/Edge hack to add some padding in between <option> elements in a native select. Chrome, and other browsers, will not allow
     * padding/margin of the option elements in a select, so this hack adds add'l disabled option elements to add that extra padding.
     * The spacer option elements are set to aria-hidden="true" so that screen readers don't announce the add'l options in the dropdown.
     * @param {any} jquerySelectElem - jquery object representing the <select> element to prettify
     */
    function _prettifyNativeSelect(jquerySelectElem) {
        DevUtils.logDebug('Prettifying native select with ID [' + jquerySelectElem.attr('id') + ']', 'A11yUtil.prettifyNativeSelect()');

        $(jquerySelectElem).addClass('ah-prettified');

        let isMac = false;

        try {
            isMac = navigator.userAgentData.platform.toUpperCase().indexOf('MAC') >= 0;
        } catch (e) {
            // using browser which does not support navigator.userAgentData... log it and leave isMac set to false (this means there is a
            // very slight possibility that a Mac user on an old version of Chrome will get their dropdown unnecessarily "prettified")
            DevUtils.logDebug('navigator.userAgentData is not supported by this browser: ' + e, 'A11yUtil.prettifyNativeSelect()');
        }

        // the CheckBrowser() function is currently implemented such that it thinks Edge is a Chrome browser (they are indeed both based off Webkit),
        // which is fine for this since Edge exhibits the same problems as Chrome
        if (CheckBrowser() === 'GC' && !isMac) {
            DevUtils.logDebug('Chrome/Edge browser detected - adding option spacers to make it look even prettier', 'A11yUtil.prettifyNativeSelect()');

            $(jquerySelectElem).find('option').each(function () {
                // add some extra left/right padding to this option
                let text = $(this).text();
                safeFind(this).html('&nbsp;&nbsp;' + text + '&nbsp;&nbsp;');

                // add the extra spacer option before this option element
                $('<option class="ah-select-spacer" disabled aria-hidden="true"></option>').insertBefore($(this));
            });

            // add a spacer at the bottom
            $('<option class="ah-select-spacer" disabled aria-hidden="true"></option>').insertAfter($('select > option:last-child:not(.ah-select-spacer)'));
        }
    }

    /**
     * Programmatically set focus on an HTML element
     * @param {string} _element - element, element ID or CSS class name
     * @param {number} [delayMs] - (OPTIONAL) delay in milliseconds for when focus is set (you might set this if you need to wait for a dialog to open)
     */
    function _setFocus(_element, delayMs) {
        let element = (typeof _element === 'object') ? _element : document.getElementById(_element);

        if (!element) {
            let elements = document.getElementsByClassName(_element);
            if (elements && elements.length > 0) {
                element = elements[0];

                if (elements.length > 1) {
                    DevUtils.logWarn('More than one element found with classname [' + _element + '], setting focus on the first instance.', 'A11yUtil.setFocus()');
                }
            }
        }

        if (element) {
            let name = $(element).attr('id');

            setTimeout(function () {
                DevUtils.logDebug('Setting focus on [' + name + ']', 'A11yUtil.setFocus()', element);
                element.focus();
            }, delayMs || 0);
        } else {
            DevUtils.logWarn('element value is null for _element ['+ _element + ']', 'A11yUtil.setFocus()');
        }
    }

    /**
     * This function will wrap <nav arial-label="xxx"></nav> tags around a given HTML element. There are many areas throughout the
     * app which require these nav tags for accessibility, and this function gives us a fairly convenient and non-invasive way of
     * adding those tags dynamically (i.e. less chance of merge conflicts by adding the nav tags directly in the HTML). This function
     * should be called inside a $(document).ready() block
     * @param {any} wrappedElemSelector CSS selector for the outer HTML element (e.g. DIV) that should be wrapped
     * @param {any} navLabel text to use for the aria-label (screen reader will announce it has "<navLabel> navigation landmark)
     */
    function _wrapNavigationArea(wrappedElemSelector, navLabel) {
        DevUtils.logDebug('Wrapping element [' + wrappedElemSelector + '] inside <nav></nav> tags', 'A11yUtil.wrapNavigationArea');
        $(wrappedElemSelector).wrap('<nav id="' +wrappedElemSelector.substring(1)+'_nav" aria-label="' + navLabel + '"></nav>');
    }

    /**
     * This function consolidates autocomplete logic.  There is a global call to set autocomplete=off for all text inputs which was implemented to avoid interference
     * with application native control auto-complete with browser auto-complete suggestions.   We'll need to work with others in Engineernig to target the specific controls
     * where we need to turn off autocomplete rather than turning off globally.
     */
    function _setAutocompleteAttributes() {
        DevUtils.logVerbose('Setting autocomplete=off attribute on all input[type=text] controls', 'A11yUtil.setAutocompleteAttributes');
        $('input[type="text"]:not([autocomplete])').attr('autocomplete', 'off');
    }

    /**
     * This function sets the aria-current attribute to false using a list of ids as the selector and sets the aria-current attribute of the selected navigation item to true.
     * @param {Object} elem navigation item that was selected
     * @param {string} idList (OPTIONAL) comma separated list of ids.   ex. '#dBDashboard,#dBMTM,#dBMembers,#dBGetAuth'
     *     */
    function _setAriaCurrentAttribute(elem, idList) {
        //if idList is passed, set aria-current attribute on all the navigation links to false
        if (idList) {
            DevUtils.logDebug('List of element ids the aria-current attribute is being set to false [' + idList + ']', 'A11yUtil.setAriaCurrentAttribute');
            $(idList).each(function () {
                $(this).attr('aria-current', 'false');
            });
        }

        //set aria-current attribute on the selected navigation link to true
        DevUtils.logDebug('Set aria-current attribute to true for id = ' + elem.id, 'A11yUtil.setAriaCurrentAttribute');
        $(elem).attr('aria-current', 'true');
    }

    /**
     * Function to enable/disable Kendo datepicker fields
     * @param {string} datepickerId - the id of the datepicker component
     * @param {boolean} isDisabled - boolean flag indicating whether you want to enable or disable the component
     */
    function _toggleKendoDatepickerDisabled(datepickerId, isDisabled) {
        let datepickerInputField = $('#' + datepickerId).find('.k-picker-wrap .k-input');
        let calendarBtn = $('#' + datepickerId + ' + .k-select[role="button"]');
        let datepickerLabel = $('label.ah-label[for="' + datepickerId + '"]');

        if (isDisabled) {
            $(datepickerInputField).attr('disabled', true).val('');
            $(calendarBtn).removeAttr('tabindex');
            $('#' + datepickerId).data('kendoDatePicker').enable(false);
            if ($(datepickerLabel).hasClass('ah-required')) {
                $(datepickerLabel).removeClass('ah-required').addClass('ah-required-disabled');
            }
        } else {
            $(datepickerInputField).attr('disabled', false);
            $(calendarBtn).attr('tabindex', '0');
            $('#' + datepickerId).data('kendoDatePicker').enable(true);
            if ($(datepickerLabel).hasClass('ah-required-disabled')) {
                $(datepickerLabel).removeClass('ah-required-disabled').addClass('ah-required');
            }
        }
    }

    ///////////////////////
    // private functions //
    ///////////////////////
    function _datepickerToggleAriaExpanded(calendarBtn, datepickerInputField) {
        let hasClass = $(calendarBtn).closest('.k-picker-wrap').hasClass('k-state-active');
        if (hasClass) {
            $(calendarBtn).attr('aria-expanded', 'true');
        } else {
            $(calendarBtn).attr('aria-expanded', 'false');
        }

        if (datepickerInputField) {
            // remove the aria-expanded on the input field that kendo adds
            $(datepickerInputField).removeAttr('aria-expanded');
        }
    }

    function _datepickerBtnKeydown(e, button) {
        if (e.keyCode === _KEYCODES.enter || e.keyCode === _KEYCODES.spacebar) {
            $(button).trigger('click');
            e.preventDefault();
        }
    }

    function _fixKendoDateButton(calendarBtn, datepickerInputField) {
        // add aria-label to calendar trigger button
        $(calendarBtn).attr('aria-label', 'calendar');

        // make the calendar button keyboard navigable and script it so Enter/Space simulate a click
        if (!$(datepickerInputField).prop('disabled')) {
            $(calendarBtn).attr('tabindex', '0');
        }

        // make the calendar button respond to Enter and Spacebar keypress
        $(calendarBtn).off('keydown').on('keydown', function (e) {
            _datepickerBtnKeydown(e, this);
        });

        // initialize aria-expanded on the trigger button
        $(calendarBtn).attr('aria-expanded', 'false');

        // set aria-expanded on trigger button when calendar button is clicked
        $(calendarBtn).on('click', function () {
            _datepickerToggleAriaExpanded(calendarBtn, datepickerInputField);
        });
    }

    function _fixKendoTimeButton(timeBtn, datepickerInputField) {
        // add aria-label to time trigger button
        $(timeBtn).attr('aria-label', 'time');

        // make the time button keyboard navigable and script it so Enter/Space simulate a click
        if (!$(datepickerInputField).prop('disabled')) {
            $(timeBtn).attr('tabindex', '0');
        }

        // make the time button respond to Enter and Spacebar keypress
        $(timeBtn).off('keydown').on('keydown', function (e) {
            _datepickerBtnKeydown(e, this);
        });

        // initialize aria-expanded on the trigger button
        $(timeBtn).attr('aria-expanded', 'false');

        // set aria-expanded on trigger button when time button is clicked
        $(timeBtn).on('click', function () {
            _datepickerToggleAriaExpanded(timeBtn, datepickerInputField);
        });
    }

    /**
     * Function that treats the enter key or space key as a click event.
     *
     * @param {any} e
     * @param {any} val
     */
    function _handleKeyboardClick(e, val) {  // NOSONAR too many instances to change/consolidate with the other function
        if (e.type === 'keydown' && (e.keyCode === A11yUtil.KEYCODES.enter || e.keyCode === A11yUtil.KEYCODES.spacebar)) {
            $(val).trigger('click');
            e.preventDefault();
        }
    }
})();
